import { Button, Spinner } from '@heroui/react';
import { PageHeader } from '@/components/common/PageHeader';
import { StatusChip } from '@/components/common/StatusChip';
import { useReady } from '@/features/dashboard/hooks/useDashboard';
import { overallTone, servicesFromReady, statusChip } from '@/features/dashboard/components/health.util';
import { ArrowPathIcon, HeartIcon } from '@/icons';

/** Live platform readiness and dependency status from the gateway health API. */
export function HealthPage() {
  const { data, isLoading, isError, isFetching, refetch } = useReady();
  const services = servicesFromReady(data);
  const overall = overallTone(data);

  return (
    <div>
      <PageHeader
        title="System health"
        description="Live availability of the Nifty Hosting platform and its services."
        actions={
          <Button
            variant="flat"
            startContent={<ArrowPathIcon className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />}
            onPress={() => void refetch()}
            isDisabled={isFetching}
          >
            Refresh
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-20"><Spinner label="Checking platform health…" /></div>
      ) : isError ? (
        <div className="rounded-xl border border-danger/30 bg-danger/5 p-8 text-center">
          <p className="font-medium text-danger">The health check could not be reached.</p>
          <p className="mt-1 text-sm text-fg-muted">Try again in a moment. This does not necessarily mean mail delivery is interrupted.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <section className="flex items-center justify-between rounded-xl border border-border bg-surface p-6">
            <div className="flex items-center gap-3">
              <HeartIcon className="h-6 w-6 text-brand" />
              <div>
                <h2 className="font-semibold text-fg">Platform status</h2>
                <p className="text-sm text-fg-muted">{services.filter((service) => service.tone === 'up').length} of {services.length} services operational</p>
              </div>
            </div>
            <StatusChip tone={statusChip[overall].tone}>{statusChip[overall].label}</StatusChip>
          </section>

          <section className="rounded-xl border border-border bg-surface">
            <div className="border-b border-border px-6 py-4">
              <h2 className="font-semibold text-fg">Services</h2>
            </div>
            {services.length === 0 ? (
              <p className="p-6 text-sm text-fg-muted">No dependency details were reported.</p>
            ) : (
              <ul className="divide-y divide-border">
                {services.map((service) => (
                  <li key={service.name} className="flex items-center justify-between px-6 py-4">
                    <span className="text-sm capitalize text-fg">{service.name.replace(/[-_]/g, ' ')}</span>
                    <StatusChip variant="dot" tone={statusChip[service.tone].tone}>{statusChip[service.tone].label}</StatusChip>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
